/**
 * 로그인이 필요한 요청에 쓰는 공용 axios 인스턴스.
 *
 * 학생 앱 대부분의 데이터 요청이 이 인스턴스를 거친다. 요청을 보낼 때 토큰을 헤더에 붙이고,
 * 토큰이 만료돼 401 이 돌아오면 새 토큰을 받아 원래 요청을 다시 보낸다.
 * 인증이 필요 없는 요청은 `axiosPublic.ts` 를 쓴다.
 *
 * 주의사항이 세 가지 있다. 셋 다 코드만 봐서는 알기 어렵다.
 *
 * 1. 이 파일은 `pages/api/` 안에 있지만 **서버에서 도는 코드가 아니다.**
 *    이 앱은 빌드할 때 정적 파일만 만들어 내서(정적 내보내기) 서버가 아예 없다.
 *    `pages/api/` 라는 위치는 관습일 뿐이고, 실제로는 브라우저에서 불리는 보통 모듈이다.
 * 2. 토큰을 `sessionStorage` 에 보관한다. 관리자 앱은 localStorage 를 쓰므로 정책이 다르다.
 *    보안 정책을 바꿀 때는 두 앱을 함께 봐야 한다.
 * 3. 토큰 갱신에 성공하면 페이지를 통째로 새로고침한다. 아래 해당 위치의 설명을 참고하라.
 */
import axios from "axios";
import { renewAccessToken } from "./login/renewAccessToken";
import { getApiBaseUrl } from "@/utils/getApiBaseUrl";

// const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL;
// const API_BASE_URL = process.env.BASE_URL;

const api = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true, // 쿠키 전송 허용
});

// 여러 요청이 동시에 401 을 받아도 토큰 갱신은 한 번만 하려고 두는 장치다.
// 갱신이 진행 중이면 나머지 요청은 failedQueue 에서 기다렸다가,
// 새 토큰이 나온 뒤 그 토큰으로 한꺼번에 다시 보내진다.
let isRefreshing = false; // 토큰 갱신 상태 플래그
let failedQueue: any[] = []; // 대기 중인 요청

/** 기다리던 요청들을 한꺼번에 깨운다. 토큰을 주면 재시도하고, 오류를 주면 모두 실패시킨다. */
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (token) {
      promise.resolve(token);
    } else {
      promise.reject(error);
    }
  });

  failedQueue = [];
};

// 요청 인터셉터: Authorization 헤더 설정
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem("accessToken");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// 응답 인터셉터: 401 오류 시 토큰 갱신
api.interceptors.response.use(
  (response) => response, // 성공적인 응답은 그대로 반환
  async (error) => {
    const originalRequest = error.config;

    // 401 오류 및 재시도 플래그 확인
    if (error.response?.status === 401 && !originalRequest._retry) {
      // 게스트(로그인 세션 없음)면 토큰 갱신을 시도하지 않는다.
      // accessToken 이 없으면 갱신용 refresh token 도 없는 상태라 renewal 은 항상 401 이고,
      // 실패 시 clear() + redirect("/login") 가 다시 인증 요청을 일으켜 무한 루프가 된다.
      if (!sessionStorage.getItem("accessToken")) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (isRefreshing) {
        // 토큰 갱신 중인 경우 대기
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true; // 갱신 플래그 활성화

      try {
        const refreshResponse = await renewAccessToken();

        // !! 이거 갱신할 때, 리코일 변수가 날아가있으면 이것도 다시 설정해줘야함
        // => 그냥 리코일 사용을 최소화하는 방향으로 가자!! (전체 리코일 변수들 체크, 모든 페이지 체크하기)
        if (refreshResponse && refreshResponse.isSuccess) {
          const newAccessToken = refreshResponse.result.accessToken;
          const userId = refreshResponse.result.userId;

          sessionStorage.setItem("accessToken", newAccessToken); // 새 토큰 저장
          sessionStorage.setItem("userId", userId);

          api.defaults.headers.Authorization = `Bearer ${newAccessToken}`; // Authorization 헤더에 새 토큰 추가
          processQueue(null, newAccessToken); // 대기 중인 요청 처리
          isRefreshing = false;

          // 새로고침을 통해 페이지 리렌더링 처리함 (리액트에서 토큰 갱신 여부를 감지하지 못하기 때문)
          // 여기서 페이지를 통째로 새로고침하므로, 바로 아래의 원래 요청 재시도는 대개 끝까지 가지 못한다.
          // 화면이 다시 그려지면서 필요한 데이터를 처음부터 새로 받아 오는 방식이다.
          window.location.reload();

          // 원래 요청 재시도
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } else {
          processQueue(new Error("토큰 갱신 실패"), null);
          isRefreshing = false;
          console.error("토큰 갱신 실패");

          const isLoggingOut = sessionStorage.getItem("isLoggingOut");
          sessionStorage.clear();

          if (!isLoggingOut) {
            // 알럿 제거됨
          }

          window.location.href = "/login";
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        console.error("토큰 갱신 중 오류:", refreshError);
        
        const isLoggingOut = sessionStorage.getItem("isLoggingOut");
        sessionStorage.clear();

        if (!isLoggingOut) {
          // 알럿 제거됨
        }

        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
